/**
 * apps/battle/client/src/Login.tsx — Student login form.
 *
 * Phase 4.2 M9-patch10: terminal/ASCII redesign matching the in-game dashboard
 * aesthetic. The form behaviour is unchanged (POSTs to /api/auth/login, calls
 * the same onLogin/onSignupRequested/onForgotPasswordRequested props) — only
 * the surrounding chrome is new.
 *
 * Boot animation:
 *   - Plays once per browser session via sessionStorage flag 'eq.login.booted'.
 *   - Sequential 'INITIALISING' → '[ OK ]', 'LOADING' → '[ OK ]', then a brief
 *     red 'WARNING:' flash. Total ~700ms.
 *   - Skippable on any keypress or click.
 *   - Subsequent navigations during the session render the fully-booted state
 *     instantly (no flash, no delay).
 */
import React, { useEffect, useState } from 'react';
import { api, ApiError } from './api.js';
import type { LoginResp, UserRole } from './api.js';
import { Spinner, ErrorBanner } from './widgets.js';

interface LoginProps {
  onLogin: (student: { id: string; email: string; name: string; role: UserRole }) => void;
  /** Optional: navigate to in-terminal signup screen (terminal mode only). */
  onSignupRequested?: () => void;
  /** Optional: navigate to in-terminal forgot-password screen. */
  onForgotPasswordRequested?: () => void;
}

const BOOT_FLAG = 'eq.login.booted';

type BootStage = 'init' | 'init-ok' | 'load' | 'load-ok' | 'warn' | 'done';

function useBootSequence(): { stage: BootStage; skip: () => void } {
  const alreadyBooted = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(BOOT_FLAG) === '1';
  const [stage, setStage] = useState<BootStage>(alreadyBooted ? 'done' : 'init');

  useEffect(() => {
    if (alreadyBooted) return;
    let cancelled = false;
    const steps: Array<{ next: BootStage; delay: number }> = [
      { next: 'init-ok', delay: 180 },
      { next: 'load', delay: 160 },
      { next: 'load-ok', delay: 180 },
      { next: 'warn', delay: 160 },
      { next: 'done', delay: 240 },
    ];
    let i = 0;
    function tick() {
      if (cancelled) return;
      if (i >= steps.length) {
        try { sessionStorage.setItem(BOOT_FLAG, '1'); } catch { /* ignore */ }
        return;
      }
      const step = steps[i++];
      setTimeout(() => {
        if (cancelled) return;
        setStage(step.next);
        if (step.next === 'done') {
          try { sessionStorage.setItem(BOOT_FLAG, '1'); } catch { /* ignore */ }
        }
        tick();
      }, step.delay);
    }
    tick();
    return () => { cancelled = true; };
  }, [alreadyBooted]);

  const skip = () => {
    setStage('done');
    try { sessionStorage.setItem(BOOT_FLAG, '1'); } catch { /* ignore */ }
  };

  return { stage, skip };
}

function stageReached(stage: BootStage, target: BootStage): boolean {
  const order: BootStage[] = ['init', 'init-ok', 'load', 'load-ok', 'warn', 'done'];
  return order.indexOf(stage) >= order.indexOf(target);
}

export function Login({ onLogin, onSignupRequested, onForgotPasswordRequested }: LoginProps) {
  const [email, setEmail] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { stage, skip } = useBootSequence();

  // Skip-on-input — any key or click while the boot is running snaps to done.
  useEffect(() => {
    if (stage === 'done') return;
    const onAny = () => skip();
    window.addEventListener('keydown', onAny, { once: true });
    window.addEventListener('mousedown', onAny, { once: true });
    return () => {
      window.removeEventListener('keydown', onAny);
      window.removeEventListener('mousedown', onAny);
    };
  }, [stage, skip]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const resp = await api.post<LoginResp>('/api/auth/login', {
        email: email.trim(),
        password: passphrase,
      });
      if (resp.authenticated && resp.student) {
        onLogin(resp.student);
      } else {
        setError('Login failed — unexpected response.');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Incorrect email or password.');
      } else if (err instanceof ApiError && err.status === 423) {
        setError('Too many failed attempts. Please try again in a few minutes.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else if (err instanceof ApiError && err.status === 503) {
        setError('Service unavailable. Please try again later.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const RULE = '================================================================================';

  return (
    <div className="login-term">
      <pre className="login-term__frame" aria-hidden="true">
        <span className="lt-rule">{RULE}</span>{'\n'}
        <span className="lt-title">{'      ECONQUEST v1.0  ·  AN EDEXCEL IGCSE ECONOMICS ADVENTURE      '}</span>{'\n'}
        <span className="lt-rule">{RULE}</span>{'\n'}
        <span className="lt-line">{' > INITIALISING UNIVERSE...'}</span>
        {stageReached(stage, 'init-ok') ? <span className="lt-ok">{'                                       [ OK ]'}</span> : <span className="lt-cursor">{' _'}</span>}
        {'\n'}
        {stageReached(stage, 'load') ? (
          <>
            <span className="lt-line">{' > LOADING ECONOMIC REALITY...'}</span>
            {stageReached(stage, 'load-ok') ? <span className="lt-ok">{'                                    [ OK ]'}</span> : <span className="lt-cursor">{' _'}</span>}
          </>
        ) : <span className="lt-line">{' '}</span>}
        {'\n'}
        {stageReached(stage, 'warn') ? (
          <span className="lt-warn">{' > WARNING: REALITY MAY DIFFER FROM TEXTBOOK.'}</span>
        ) : <span className="lt-line">{' '}</span>}
        {'\n'}
        <span className="lt-rule">{RULE}</span>{'\n'}
        {'\n'}
        <span className="lt-hero">{"                                DON'T  PANIC."}</span>{'\n'}
        {'\n'}
        <span className="lt-line">{"   The Hitchhiker's Guide to IGCSE Economics would probably say this:"}</span>{'\n'}
        {'\n'}
        <span className="lt-quote">{'   "Unlike Physics or Chemistry — which demand that students memorise an'}</span>{'\n'}
        <span className="lt-quote">{'    alarming number of formulae before breakfast — Economics is, at its'}</span>{'\n'}
        <span className="lt-quote">{'    core, a surprisingly compact subject. The catch, naturally, is that'}</span>{'\n'}
        <span className="lt-quote">{"    the exam doesn't simply ask you to recite concepts. It asks you to"}</span>{'\n'}
        <span className="lt-quote">{'    CHAIN them together. Across the syllabus. In response to a question'}</span>{'\n'}
        <span className="lt-quote">{"    about something happening, right now, on a planet you've never visited."}</span>{'\n'}
        <span className="lt-quote">{"    Economists call this 'analysis'. Examiners call this 'the bit most"}</span>{'\n'}
        <span className="lt-quote">{"    students get wrong'. The Guide calls it a "}</span>
        <span className="lt-accent">{'Thursday'}</span>
        <span className="lt-quote">{'."'}</span>{'\n'}
        <span className="lt-rule">{RULE}</span>{'\n'}
        <span className="lt-line">{'  YOUR MISSION:  Master concepts.  Chain arguments.  Survive both papers.'}</span>{'\n'}
        <span className="lt-line">{'                    The exam board sends no search party.'}</span>{'\n'}
        <span className="lt-hero-sm">{'                  KEEP CALM AND CARRY ON.   '}</span>
        <span className="lt-hero-sm">{"DON'T PANIC."}</span>{'\n'}
        <span className="lt-rule">{RULE}</span>
      </pre>

      <form className="login-term__form" onSubmit={handleSubmit} autoComplete="on">
        <div className="ltf-row">
          <label className="ltf-label" htmlFor="email">{' EMAIL    >'}</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="student@school.com"
            required
            className="ltf-input"
          />
        </div>
        <div className="ltf-row">
          <label className="ltf-label" htmlFor="passphrase">{' ACCESS   >'}</label>
          <input
            id="passphrase"
            type="password"
            autoComplete="current-password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="••••••••"
            required
            className="ltf-input"
          />
        </div>
        {error && <div className="ltf-error"><ErrorBanner msg={error} /></div>}
        <div className="ltf-actions">
          <button className="ltf-btn ltf-btn--primary" type="submit" disabled={loading}>
            {loading ? <Spinner size={14} /> : '[ > JACK IN ]'}
          </button>
        </div>
        {(onSignupRequested || onForgotPasswordRequested) && (
          <div className="ltf-links">
            {onForgotPasswordRequested ? (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onForgotPasswordRequested(); }}
                className="ltf-link"
              >
                [ FORGOT PASSWORD ]
              </a>
            ) : <span />}
            {onSignupRequested ? (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); onSignupRequested(); }}
                className="ltf-link"
              >
                [ CREATE ACCOUNT ]
              </a>
            ) : <span />}
          </div>
        )}
      </form>
    </div>
  );
}
